import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faPaste,
  faArrowUp,
  faArrowDown,
  faLevelUpAlt,
  faLevelDownAlt,
  faSyncAlt,
  faExchangeAlt,
  faEraser,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import CommonButton from "../elments/CommonButton";
import { useEscapeKey } from "./hooks/useEscapeKey";
import "./styles/PasteModal.css";

const PasteModal = ({ 
  isOpen, 
  onClose, 
  onPaste, 
  onAction, 
  label,
  focusedRowIndex,
  initialContent = "",
  preserveOriginalIndices = false,
  onPreserveIndicesChange
}) => {
  const [pasteText, setPasteText] = useState(initialContent);
  const [localPreserveIndices, setLocalPreserveIndices] = useState(preserveOriginalIndices);
  const editableRef = useRef(null);

  useEffect(() => {
    if (initialContent && editableRef.current) {
      editableRef.current.innerHTML = initialContent;
      setPasteText(initialContent);
    }
  }, [initialContent]);

  useEffect(() => {
    if (editableRef.current && editableRef.current.innerHTML !== pasteText) {
      const selection = window.getSelection();
      const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

      if (editableRef.current.innerHTML !== pasteText) {
        editableRef.current.innerHTML = pasteText;

        if (range && editableRef.current.contains(range.startContainer)) {
          try {
            selection.removeAllRanges();
            selection.addRange(range);
          } catch (e) {
            const newRange = document.createRange();
            newRange.selectNodeContents(editableRef.current);
            newRange.collapse(false);
            selection.removeAllRanges();
            selection.addRange(newRange);
          }
        }
      }
    }
  }, [pasteText]);

  const handlePaste = (e) => {
    e.preventDefault();

    const htmlContent = e.clipboardData.getData("text/html");
    const plainContent = e.clipboardData.getData("text/plain");

    let cleaned;
    if (htmlContent) {
      cleaned = cleanPastedHTML(htmlContent);
    } else {
      cleaned = plainContent
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line)
        .map((line) => `<div>${line}</div>`)
        .join("");
    }

    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      range.deleteContents();

      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = cleaned;

      const frag = document.createDocumentFragment();
      while (tempDiv.firstChild) {
        frag.appendChild(tempDiv.firstChild);
      }

      range.insertNode(frag);
      range.collapse(false);

      setPasteText(editableRef.current.innerHTML);
      onPaste(editableRef.current.innerHTML);
    }
  };

  const handleAction = (mode) => {
    // Pass the preserve indices state to the parent
    onAction(mode, localPreserveIndices);
  };

  const handlePreserveIndicesChange = (e) => {
    const newValue = e.target.checked;
    setLocalPreserveIndices(newValue);
    if (onPreserveIndicesChange) {
      onPreserveIndicesChange(newValue);
    }
  };

  const cleanPastedHTML = (dirty) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(dirty, "text/html");

    const allowedTags = [
      "B", "I", "U", "STRONG", "EM", "BR", "P", "DIV", "OL", "UL", "LI", "SPAN",
    ];

    const allowedStyles = [
      "color", "background-color", "font-weight", "font-style", "text-decoration",
    ];

    const cleanNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        let text = node.textContent
          .replace(/\u00A0/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        return text || "";
      }

      if (["TABLE", "TBODY", "TR", "TD", "TH"].includes(node.nodeName)) {
        const content = Array.from(node.childNodes)
          .map(cleanNode)
          .filter((text) => text.trim())
          .join(" ")
          .replace(/\s{2,}/g, " ")
          .trim();
        return content;
      }

      if (node.nodeName === "OL" || node.nodeName === "UL") {
        const listItems = Array.from(node.querySelectorAll("li"))
          .map((li) => {
            const content = Array.from(li.childNodes)
              .map(cleanNode)
              .join("")
              .trim();
            return content ? `<div>${content}</div>` : "";
          })
          .filter((item) => item)
          .join("");
        return listItems;
      }

      if (node.nodeName === "LI") {
        const content = Array.from(node.childNodes)
          .map(cleanNode)
          .join("")
          .trim();
        return content ? `<div>${content}</div>` : "";
      }

      if (
        node.nodeName === "SPAN" &&
        (!node.innerHTML.trim() || node.classList.contains("MsoNormal"))
      ) {
        return Array.from(node.childNodes).map(cleanNode).join("");
      }

      if (
        node.nodeType === Node.COMMENT_NODE ||
        node.nodeType === Node.PROCESSING_INSTRUCTION_NODE ||
        node.nodeName === "STYLE" ||
        node.nodeName === "SCRIPT" ||
        node.nodeName === "META"
      ) {
        return "";
      }

      if (!allowedTags.includes(node.nodeName)) {
        return Array.from(node.childNodes).map(cleanNode).join("");
      }

      if (node.hasAttribute("class")) {
        const classes = node.getAttribute("class").split(" ");
        const wordClasses = ["MsoNormal", "MsoListParagraph", "MsoBodyText"];
        const filteredClasses = classes.filter(
          (cls) => !wordClasses.includes(cls)
        );
        if (filteredClasses.length > 0) {
          node.setAttribute("class", filteredClasses.join(" "));
        } else {
          node.removeAttribute("class");
        }
      }

      const wordAttributes = ["lang", "style", "type", "start"];
      wordAttributes.forEach((attr) => {
        if (node.hasAttribute(attr)) {
          node.removeAttribute(attr);
        }
      });

      if (node.style && node.style.length > 0) {
        const styles = [];
        allowedStyles.forEach((styleProp) => {
          const value = node.style.getPropertyValue(styleProp);
          if (value) styles.push(`${styleProp}: ${value}`);
        });
        if (styles.length > 0) {
          node.setAttribute("style", styles.join("; "));
        } else {
          node.removeAttribute("style");
        }
      }

      const childContent = Array.from(node.childNodes)
        .map(cleanNode)
        .filter((text) => text.trim())
        .join("");

      if (!childContent.trim()) {
        return "";
      }

      if (["P", "DIV"].includes(node.nodeName) && childContent.trim()) {
        return `<${node.nodeName.toLowerCase()}>${childContent}</${node.nodeName.toLowerCase()}>`;
      }

      if (
        ["B", "I", "U", "STRONG", "EM", "SPAN"].includes(node.nodeName) &&
        childContent.trim()
      ) {
        if (node.nodeName === "SPAN" && !node.getAttribute("style")) {
          return childContent;
        }
        return `<${node.nodeName.toLowerCase()}>${childContent}</${node.nodeName.toLowerCase()}>`;
      }

      return childContent;
    };

    let cleanedContent = Array.from(doc.body.childNodes)
      .map(cleanNode)
      .filter((text) => text.trim())
      .join("");

    if (!cleanedContent.includes("<div>") && !cleanedContent.includes("<li>")) {
      const lines = cleanedContent.split(/\n/);
      const processedLines = lines
        .map((line) => {
          line = line.replace(/^\s*\d+\.\s*/, "").trim();
          line = line.replace(/^[•\-]\s*/, "").trim();
          return line;
        })
        .filter((line) => line.trim());

      if (processedLines.length > 1) {
        cleanedContent = processedLines
          .map((line) => `<div>${line}</div>`)
          .join("");
      }
    }

    cleanedContent = cleanedContent
      .replace(/\n\s*\n/g, "\n")
      .replace(/(<br\s*\/?>\s*){2,}/gi, "<br><br>")
      .trim();

    return cleanedContent || "";
  };

  useEscapeKey(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="pastemodal-overlay" onClick={onClose}>
      <div className="pastemodal-content" onClick={(e) => e.stopPropagation()}>
        <div className="pastemodal-header">
          <h3>
            <FontAwesomeIcon icon={faPaste} style={{ marginRight: "8px" }} />
            Paste {label}
          </h3>
          <button className="pastemodal-close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="pastemodal-body">
          <p className="pastemodal-help">
            Paste your {label.toLowerCase()} below (from Excel, Word, or plain text), then choose
            how to apply it to the list.
          </p>

          <div className="pastemodal-field-block">
            <label htmlFor="pastemodal-editable">Content</label>
            <div
              id="pastemodal-editable"
              ref={editableRef}
              className="pastemodal-editable"
              contentEditable
              onInput={(e) => {
                setPasteText(e.currentTarget.innerHTML);
                onPaste(e.currentTarget.innerHTML);
              }}
              onPaste={handlePaste}
              data-placeholder={`Paste your ${label.toLowerCase()} here...`}
            />
          </div>

          <div className="pastemodal-option-card">
            <label className="pastemodal-option-label">
              <input
                type="checkbox"
                checked={localPreserveIndices}
                onChange={handlePreserveIndicesChange}
              />
              <span>Preserve original indices from pasted items</span>
            </label>
          </div>

          <p className="pastemodal-section-label">Apply as</p>
          <div className="pastemodal-action-grid">
            <CommonButton
              label="Prepend"
              symbol={faArrowUp}
              title="Add items before all existing items"
              onClick={() => handleAction("prepend")}
              variant="tab"
            />
            <CommonButton
              label="Append"
              symbol={faArrowDown}
              title="Add items after all existing items"
              onClick={() => handleAction("append")}
              variant="tab"
            />
            <CommonButton
              label="Insert Before"
              symbol={faLevelUpAlt}
              title="Insert items before selected row"
              onClick={() => handleAction("insertBefore")}
              variant="tab"
              disabled={focusedRowIndex === null}
            />
            <CommonButton
              label="Insert After"
              symbol={faLevelDownAlt}
              title="Insert items after selected row"
              onClick={() => handleAction("insertAfter")}
              variant="tab"
              disabled={focusedRowIndex === null}
            />
            <CommonButton
              label="Replace"
              symbol={faSyncAlt}
              title="Replace all existing items"
              onClick={() => handleAction("replace")}
              variant="tab"
            />
            <CommonButton
              label="Replace Current Row"
              symbol={faExchangeAlt}
              title="Replace Current Row"
              onClick={() => handleAction("replacerc")}
              variant="tab"
            />
          </div>

          {focusedRowIndex === null && (
            <div className="pastemodal-warning">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>Insert Before/After options require clicking on a row first</span>
            </div>
          )}
        </div>

        <div className="pastemodal-footer">
          <CommonButton
            label="Clear"
            symbol={faEraser}
            title="Clear the clipboard"
            onClick={() => {
              setPasteText("");
              onPaste("");
            }}
            variant="secondary"
          />
          <CommonButton
            label="Cancel"
            symbol={faTimes}
            title="Cancel"
            onClick={onClose}
            variant="outline-secondary"
          />
        </div>
      </div>
    </div>
  );
};

export default PasteModal;